/**
 * Parses transaction errors and provides user-friendly messages.
 * This handles common transaction errors like insufficient balance, gas estimation failures, etc.
 * @param error an error from the transaction provider
 * @param fallbackMessage optional fallback message if no specific error is detected
 */
export function transactionErrorToUserReadableMessage(error: any, fallbackMessage?: string): string {
  let reason: string | undefined

  // Handle error code 4001 (user rejected transaction)
  if (error.code) {
    switch (error.code) {
      case 4001:
        return 'Transaction rejected'
    }
  }

  console.error('Transaction error:', error)

  // Extract the error reason/message by drilling down through nested errors
  while (error) {
    // Check multiple possible locations for the error message
    // Prefer data.message for JSON-RPC errors as it contains the detailed error
    const currentReason = error.data?.message ?? error.reason ?? error.message
    if (currentReason) {
      reason = currentReason
    }

    const nextError = error.error ?? error.data?.originalError
    if (!nextError || nextError === error) {
      // No deeper error or same error (prevent infinite loop)
      break
    }
    error = nextError
  }

  // Check for user rejection patterns (for wallets that don't use code 4001)
  if (
    // For Rainbow:
    (reason?.match(/request/i) && reason?.match(/reject/i)) ||
    // For Frame:
    reason?.match(/declined/i) ||
    // For SafePal:
    reason?.match(/cancelled by user/i) ||
    // For Coinbase:
    reason?.match(/user denied/i)
  ) {
    return 'Transaction rejected'
  }

  // Check for gas-related errors
  // These could be due to insufficient balance OR other issues (contract errors, etc.)
  if (
    reason?.match(/out of gas/i) ||
    reason?.match(/gas required exceeds allowance/i) ||
    reason?.match(/cannot estimate gas/i) ||
    reason?.match(/unable to apply transaction/i) ||
    reason?.match(/not enough gas supplied for intrinsic gas costs/i) ||
    reason?.match(/highest gas limit/i)
  ) {
    return 'Unable to estimate gas for this transaction. You may have insufficient balance or the transaction may fail.'
  }

  // Only show "insufficient balance" when we're very certain it's a balance issue
  if (reason?.match(/insufficient.*funds/i) || reason?.match(/sender doesn't have enough funds/i)) {
    return 'Insufficient balance to complete this transaction. Please add funds to your wallet.'
  }

  // Check for execution reverted errors
  if (reason?.indexOf('execution reverted: ') === 0) {
    reason = reason.substr('execution reverted: '.length)
  }

  // Check for nonce errors
  if (reason?.match(/nonce.*too low/i) || reason?.match(/replacement transaction underpriced/i)) {
    return 'Transaction nonce error. Please try again or reset your wallet.'
  }

  // Check for deadline/slippage errors
  if (reason?.match(/deadline/i) || reason?.match(/expired/i)) {
    return 'The transaction could not be sent because the deadline has passed. Please try again.'
  }

  if (reason?.match(/slippage/i) || reason?.match(/price.*movement/i)) {
    return 'This transaction will not succeed due to price movement. Try increasing your slippage tolerance.'
  }

  // Return the fallback message or the extracted reason
  return fallbackMessage ?? reason ?? 'Unknown error occurred. Please try again.'
}
